import React from "react";
import { Alert } from "../../../../ui/components/Alert";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { Table } from "../../../../ui/components/Table";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherAlertTriangle } from "@subframe/core";
import { FeatherEdit2 } from "@subframe/core";
import { FeatherSave } from "@subframe/core";

export interface ChangeItem {
  field: string;
  previousValue: string;
  newValue: string;
}

interface ReviewChangesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  changes: ChangeItem[];
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  description?: string;
  /** Optional class for the inner content (width, padding) */
  contentClassName?: string;
}

const ReviewChangesModal: React.FC<ReviewChangesModalProps> = ({
  open,
  onOpenChange,
  changes,
  onConfirm,
  onCancel,
  title = "Review Changes",
  description = "These changes will affect the invoice calculations and tax implications. Please review carefully before proceeding.",
  contentClassName,
}) => {
  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  // Filter out line item changes with source/destination of supply
  const filteredChanges = changes.filter((change) => {
    const fieldLower = change.field.toLowerCase();
    // Ignore Line Item Source/Destination of Supply changes
    if (
      change.field.startsWith("Line Item ") &&
      (fieldLower.includes("source of supply") ||
        fieldLower.includes("destination of supply"))
    ) {
      return false;
    }
    return true;
  });

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div
        className={`flex flex-col items-start gap-6 bg-default-background ${
          contentClassName ?? "w-112 px-6 py-6"
        }`}
      >
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWithBackground
              variant="warning"
              size="medium"
              icon={<FeatherEdit2 />}
            />
            <span className="text-heading-2 font-heading-2 text-default-font">
              {title}
            </span>
          </div>
          <Badge variant="warning">{filteredChanges.length} Changes</Badge>
        </div>
        <div className="flex w-full flex-col items-start gap-4">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Field</Table.HeaderCell>
                <Table.HeaderCell>Previous Value</Table.HeaderCell>
                <Table.HeaderCell>New Value</Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {filteredChanges.map((change, index) => (
              <Table.Row key={index}>
                <Table.Cell>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {change.field}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-neutral-500">
                    {change.previousValue}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-brand-600">
                    {change.newValue}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>
        </div>
        <Alert
          variant="warning"
          icon={<FeatherAlertTriangle />}
          title=""
          description={description}
        />
        <div className="flex w-full items-center justify-end gap-3">
          <Button variant="neutral-tertiary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button icon={<FeatherSave />} onClick={handleConfirm}>
            Save Changes
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ReviewChangesModal;
